import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Form, Input, InputNumber, Modal, Tag } from 'antd';
import { EyeOutlined, SendOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { errorText } from './helpers';

/** The text, the company's details and the sender, with a preview and the test mail. */
export default function TemplatePanel({ companyId, request, canWrite, onChanged }) {
  const { t } = useTranslation('partnerRequests');
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [testTo, setTestTo] = useState('');
  const [preview, setPreview] = useState(null);
  const requestId = request._id;
  const locked = request.locked || !canWrite;

  useEffect(() => {
    form.setFieldsValue({
      subject: request.subject,
      body: request.body,
      companyInfo: request.companyInfo,
      fromEmail: request.fromEmail,
      fromName: request.fromName,
      replyTo: request.replyTo,
      deadlineDays: request.deadlineDays,
    });
  }, [request, form]);

  const applied = (data) => {
    queryClient.setQueryData(['partnerRequests', requestId, 'one'], data);
    onChanged();
  };

  const save = useMutation({
    mutationFn: (values) => API.partnerRequests.updateTemplate(companyId, requestId, values),
    onSuccess: (data) => {
      message.success(t('template.saved'));
      applied(data);
    },
  });
  const test = useMutation({
    mutationFn: () => API.partnerRequests.sendTest(companyId, requestId, { to: testTo }),
    onSuccess: (data) => {
      message.success(t('template.testQueued', { to: testTo }));
      applied(data);
    },
  });
  const show = useMutation({
    mutationFn: () => API.partnerRequests.previewMail(companyId, requestId),
    onSuccess: setPreview,
  });

  const error = save.error || test.error || show.error;

  return (
    <div className="grid gap-4">
      {request.locked && <Alert type="info" showIcon message={t('template.locked')} />}
      {error && <Alert type="error" showIcon message={errorText(t, error)} />}
      <Form form={form} layout="vertical" disabled={locked} onFinish={(values) => save.mutate(values)}>
        <div className="grid gap-4 lg:grid-cols-3">
          <Card size="small" className="lg:col-span-2" title={t('template.text')}>
            <Form.Item name="subject" label={t('template.subject')} rules={[{ required: true, message: t('required') }]}>
              <Input maxLength={500} />
            </Form.Item>
            <Form.Item name="body" label={t('template.body')} rules={[{ required: true, message: t('required') }]}>
              <Input.TextArea rows={18} maxLength={10000} />
            </Form.Item>
            <div className="text-xs opacity-70">
              <div className="mb-1">{t('template.tokensHelp')}</div>
              {request.tokens.map((token) => (
                <Tag key={token} className="mb-1">
                  {`{{${token}}}`} · {t(`tokens.${token}`)}
                </Tag>
              ))}
            </div>
          </Card>
          <div className="grid content-start gap-4">
            <Card size="small" title={t('template.sender')}>
              <Form.Item
                name="fromEmail"
                label={t('template.fromEmail')}
                extra={t('template.fromHelp')}
                rules={[{ required: true, message: t('required') }, { type: 'email', message: t('errors.invalid_email') }]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="fromName" label={t('template.fromName')}>
                <Input maxLength={120} />
              </Form.Item>
              <Form.Item
                name="replyTo"
                label={t('template.replyTo')}
                extra={t('template.replyToHelp')}
                rules={[{ type: 'email', message: t('errors.invalid_email') }]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="deadlineDays" label={t('template.deadlineDays')} extra={t('template.deadlineHelp')}>
                <InputNumber min={1} max={60} className="w-full" />
              </Form.Item>
            </Card>
            <Card size="small" title={t('template.company')}>
              {['name', 'address', 'pib', 'registrationNo'].map((field) => (
                <Form.Item key={field} name={['companyInfo', field]} label={t(`template.companyFields.${field}`)}>
                  <Input maxLength={300} />
                </Form.Item>
              ))}
            </Card>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button shape="round" icon={<EyeOutlined />} disabled={false} loading={show.isPending} onClick={() => show.mutate()}>
            {t('template.preview')}
          </Button>
          {!locked && (
            <Button shape="round" type="primary" htmlType="submit" loading={save.isPending}>
              {t('template.save')}
            </Button>
          )}
        </div>
      </Form>

      {!locked && (
        <Card size="small" title={t('template.test')}>
          <p className="mb-2 text-sm opacity-70">{t('template.testHelp')}</p>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Input
              className="min-w-0 flex-1"
              style={{ minWidth: 240 }}
              value={testTo}
              placeholder={t('template.testPlaceholder')}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <Button
              shape="round"
              icon={<SendOutlined />}
              disabled={!testTo.trim()}
              loading={test.isPending}
              onClick={() => test.mutate()}
            >
              {t('template.testSend')}
            </Button>
          </div>
          {request.testSentAt && (
            <p className="mt-2 text-sm">
              <Tag color={request.testValid ? 'green' : 'gold'}>
                {request.testValid ? t('template.testValid') : t('template.testStale')}
              </Tag>
              {t('template.testLast', { to: request.testSentTo, at: new Date(request.testSentAt).toLocaleString('sr-RS') })}
            </p>
          )}
        </Card>
      )}

      <Modal
        open={Boolean(preview)}
        width={760}
        title={preview?.subject}
        onCancel={() => setPreview(null)}
        footer={
          <Button shape="round" onClick={() => setPreview(null)}>
            {t('close')}
          </Button>
        }
      >
        {preview && (
          <>
            <p className="mb-2 text-sm opacity-70">
              {t('template.previewFor', { from: preview.from || '—', replyTo: preview.replyTo || '—', name: preview.recipient.name })}
            </p>
            <iframe title="preview" sandbox="" srcDoc={preview.html} className="h-[60vh] w-full rounded border-0" />
          </>
        )}
      </Modal>
    </div>
  );
}
